import { convertColumnWidthMapToStoredWidths } from 'frontend/columns/functions/convertColumnWidthMapToStoredWidths'
import { convertStoredWidthsToColumnWidthMap } from 'frontend/columns/functions/convertStoredWidthsToColumnWidthMap'
import { IColumnPresetData } from 'frontend/columns/types/IColumnPresetData'
import { IStoredUnsavedColumnPresetItem } from 'frontend/columns/types/IStoredUnsavedColumnPresetItem'
import { IUnsavedColumnPresetItem } from 'frontend/columns/types/IUnsavedColumnPresetItem'
import { useSortItems } from 'frontend/common/composables/useSortItems'
import { DataModelType } from 'frontend/roles/enum/DataModelType'
import localForage from 'localforage'
import { computed, onMounted, type Ref, ref, watch } from 'vue'

export function useUnsavedColumnPresetItems(
  dataModelType: DataModelType,
  currentColumnPresetId: Ref<string | true>,
  selectedSubtypeForColumns?: string,
) {
  const { convertStoredSortToSortItems, convertSortItemsToStoredSort } = useSortItems()

  const unsavedColumnPresetItems = ref<IUnsavedColumnPresetItem[]>([])

  const currentUnsavedColumnPresetData = computed<IColumnPresetData>({
    get() {
      return (
        unsavedColumnPresetItems.value.find(presetItem => {
          return presetItem.columnPresetId === currentColumnPresetId.value
        })?.data || null
      )
    },
    set(newValue: IColumnPresetData) {
      const unsavedColumnPresetItem = unsavedColumnPresetItems.value.find(preset => {
        return preset.columnPresetId === currentColumnPresetId.value
      })

      if (unsavedColumnPresetItem) {
        unsavedColumnPresetItems.value = unsavedColumnPresetItems.value.map(preset => {
          if (preset.columnPresetId === currentColumnPresetId.value) {
            return {
              columnPresetId: currentColumnPresetId.value,
              data: newValue,
            }
          }
          return preset
        })
      } else {
        unsavedColumnPresetItems.value = [
          ...unsavedColumnPresetItems.value,
          {
            columnPresetId: currentColumnPresetId.value,
            data: newValue,
          },
        ]
      }
    },
  })

  const persistedCurrentUnsavedColumnPresetData = computed<IStoredUnsavedColumnPresetItem[]>({
    get() {
      return unsavedColumnPresetItems.value.map(e => {
        return {
          columnPresetId: e.columnPresetId,
          data: {
            visibleColumnTypes: e.data.visibleColumnTypes || undefined,
            pinnedColumnTypes: e.data.pinnedColumnTypes,
            name: e.data.name || undefined,
            sortItems: convertSortItemsToStoredSort(e.data.sortItems) || undefined,
            columnWidths: convertColumnWidthMapToStoredWidths(e.data.columnWidthsMap) || undefined,
          },
        }
      })
    },
    set(value: IStoredUnsavedColumnPresetItem[]) {
      unsavedColumnPresetItems.value = value.map(e => {
        return {
          columnPresetId: e.columnPresetId,
          data: {
            visibleColumnTypes: e.data.visibleColumnTypes || undefined,
            pinnedColumnTypes: e.data.pinnedColumnTypes || undefined,
            name: e.data.name || undefined,
            sortItems: e.data.sortItems
              ? convertStoredSortToSortItems(e.data.sortItems)
              : undefined,
            columnWidthsMap: e.data.columnWidths
              ? convertStoredWidthsToColumnWidthMap(e.data.columnWidths)
              : undefined,
          },
        }
      })
    },
  })

  const unsavedDataWasLoaded = ref(false)
  watch(persistedCurrentUnsavedColumnPresetData, async value => {
    if (!unsavedDataWasLoaded.value) {
      return
    }
    let key = `${dataModelType}`
    if (selectedSubtypeForColumns?.length) {
      key += `:${selectedSubtypeForColumns}`
    }
    await localForage.setItem(`unsavedColumnPresetItems:${key}`, JSON.stringify(value))
  })

  onMounted(async () => {
    if (!dataModelType) {
      return
    }

    let key = `${dataModelType}`
    if (selectedSubtypeForColumns?.length) {
      key += `:${selectedSubtypeForColumns}`
    }
    const unsavedPresetValuesFromStorage = await localForage.getItem(
      `unsavedColumnPresetItems:${key}`,
    )
    if (unsavedPresetValuesFromStorage) {
      persistedCurrentUnsavedColumnPresetData.value = JSON.parse(
        unsavedPresetValuesFromStorage as string,
      )
    }
    unsavedDataWasLoaded.value = true
  })

  return {
    unsavedColumnPresetItems,
    currentUnsavedColumnPresetData,
  }
}
